
















import { computed, defineComponent, onBeforeUnmount, ref } from "@vue/composition-api";
import { CreditCardAssignment } from "@scrile/api-provider/dist/api/CreditCardAssignmentsProvider";
import { RecurringBillingScheduleSubjectTypeEnum } from "@scrile/api-provider/dist/api/RecurringBillingScheduleProvider";
import { Subject } from "@scrile/api-provider/dist/api/SubjectProvider";
import { Subscription } from "@scrile/api-provider/dist/api/SubscriptionProvider";
import { Transaction } from "@scrile/api-provider/dist/api/TransactionProvider";
import useAuthorisedOnly from "@/hooks/useAuthorisedOnly";
import useAuthUser from "@/hooks/useAuthUser";
import useSystemSettings from "@/hooks/useSystemSettings";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { getMainCurrencyCode } from "@/lib/moneyHelpers";
import getIntegration from "@/payments/integrations";
import { EVENTS } from "@/const";
import ModalView from "@/components/ModalView/ModalView.vue";

export default defineComponent({
  name: "PurchaseManager",
  components: { ModalView },
  setup() {
    const { authUserStrict } = useAuthUser();
    const show = ref(false);
    const loading = ref(false);
    const processingRequest = ref(false);
    const { creditCardAllowed, currentPaymentSystem } = useSystemSettings();
    const pricePackageId = ref<string>("");
    const price = ref("");
    const subscriptionSubject = ref<Subject<RecurringBillingScheduleSubjectTypeEnum> | null>(null);
    const isSubscription = computed(() => !!subscriptionSubject.value);

    const integrationModule = computed(() => getIntegration(currentPaymentSystem.value));

    const closeOnError = ref(false);
    const closeModal = (payload?: Transaction | Subscription) => {
      emitter.emit(!isSubscription.value ? EVENTS.PAYMENT_PURCHASED : EVENTS.PAYMENT_SUBSCRIBED, payload);
      pricePackageId.value = "";
      price.value = "";
      show.value = false;
      subscriptionSubject.value = null;
      closeOnError.value = false;
    };

    const onClose = () => {
      if (processingRequest.value) return;
      closeModal();
    };

    const startPurchase = async (data?: any) => {
      try {
        processingRequest.value = true;
        if (!pricePackageId.value || !currentPaymentSystem.value) {
          throw new Error("Cant find payment information");
        }
        let payload: Transaction | Subscription;
        if (subscriptionSubject.value) {
          payload = await integrationModule.value.subscribe(
            {
              currency: getMainCurrencyCode(),
              paymentSystemId: currentPaymentSystem.value.id,
              subject: subscriptionSubject.value,
            },
            data
          );
        } else {
          payload = await integrationModule.value.purchase(
            {
              currency: getMainCurrencyCode(),
              paymentSystemId: currentPaymentSystem.value.id,
              pricePackageId: pricePackageId.value,
            },
            data
          );
        }
        closeModal(payload);
      } finally {
        processingRequest.value = false;
      }
    };

    const onSubmit = (data?: any) =>
      startPurchase(data).catch(e => {
        // eslint-disable-next-line no-console
        console.error(e);
        if (closeOnError.value) {
          closeModal();
        }
      });

    const onStartPurchase = useAuthorisedOnly(
      async ({
        pricePackageId: newPricePackageId,
        price: newPrice = "",
        subscriptionSubject: newSubscriptionSubject,
      }: {
        pricePackageId: string;
        price?: string;
        subscriptionSubject?: Subject<RecurringBillingScheduleSubjectTypeEnum>;
      }) => {
        price.value = newPrice;
        pricePackageId.value = newPricePackageId;
        subscriptionSubject.value = newSubscriptionSubject ?? null;
        try {
          loading.value = true;
          let cardsAssignmentData: CreditCardAssignment[] = [];
          if (creditCardAllowed.value) {
            cardsAssignmentData = await providers.CreditCardAssignmentsProvider.findAll({
              userId: authUserStrict.value.id,
            });
          }
          if (cardsAssignmentData.length > 0) {
            try {
              await startPurchase();
            } catch (e) {
              // eslint-disable-next-line no-console
              console.error(e);
              closeModal();
            }
            return;
          }
          show.value = true;
        } finally {
          loading.value = false;
        }
      },
      ""
    );
    emitter.on<any>(EVENTS.PAYMENT_START_PURCHASE, onStartPurchase);

    onBeforeUnmount(() => {
      emitter.off<any>(EVENTS.PAYMENT_START_PURCHASE, onStartPurchase);
    });

    return {
      loading,
      show,
      processingRequest,
      integrationModule,
      price,
      currentPaymentSystem,
      isSubscription,
      closeOnError,
      onSubmit,
      onClose,
    };
  },
});
